// Главная: приветствие, переписка с помощником, поле ввода, быстрые действия и шаблоны.
import { Fragment, useEffect, useRef, type MouseEvent } from "react";
import { boot, MARK_URL } from "../boot";
import { ActionArt } from "../components/ActionArt";
import { PreviewDialog, SourceDialog } from "../components/Dialogs";
import { Icon } from "../components/Icon";
import { Sheets } from "../components/Sheets";
import { useTabs } from "../components/Tabs";
import { createChat, QuestionClosed, type Chat } from "../chat/controller";
import { onLeave } from "../router";
import { composerStore, messagesStore, reviewStore, type Message } from "../chat/state";
import { plural } from "../lib/text";
import { useStore } from "../store";

// Новое сообщение видно сразу: длинное — с начала, короткое — внизу страницы.
// Рядом открыт документ — прокручивается не страница, а колонка чата.
function reveal(node: HTMLElement | null) {
  if (!node) return;
  if (node.offsetHeight > window.innerHeight * 0.6) {
    node.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }
  const column = node.closest<HTMLElement>(".content");
  const box = column && getComputedStyle(column).overflowY === "auto" ? column : document.documentElement;
  box.scrollTo({ top: box.scrollHeight, behavior: "smooth" });
}

function MessageView({ message }: { message: Message }) {
  const ref = useRef<HTMLDivElement>(null);
  // Тело эффекта — блоком: в новом Chrome scrollIntoView и scrollTo возвращают Promise, и React принял бы его
  // за функцию очистки — при исчезновении сообщения (открыли разбор) падал бы весь интерфейс.
  useEffect(() => { reveal(ref.current); }, []);
  if (message.role === "user") {
    return <div ref={ref} className="msg msg-user"><div className="bubble">{message.text}</div></div>;
  }
  const q = message.question;
  const hasButtons = q && (q.choices.length || q.skip || q.extra.length);
  return (
    <div ref={ref} className="msg msg-bot">
      <div className="avatar" aria-hidden="true"><img src={MARK_URL} alt="" width={22} height={22} /></div>
      <div className="bubble">
        <div className="bot-name" aria-hidden="true">Правосвод</div>
        {message.parts}
        {q && hasButtons ? (
          <div className="chips">
            <QuestionClosed.Provider value={q.closed}>
              {q.choices.map((c) => (
                <button key={c.value} type="button" className={`chip${q.chosen === c.label ? " chosen" : ""}`} disabled={q.closed}
                  onClick={() => q.choose(c.value, c.label)}>{c.label}</button>
              ))}
              {q.skip && (
                <button type="button" className={`chip chip-quiet${q.chosen === q.skip ? " chosen" : ""}`} disabled={q.closed}
                  onClick={() => q.choose("", q.skip!)}>{q.skip}</button>
              )}
              {q.extra.map((node, i) => <Fragment key={i}>{node}</Fragment>)}
            </QuestionClosed.Provider>
          </div>
        ) : null}
      </div>
      {message.footer && <div className="msg-footer">{message.footer}</div>}
    </div>
  );
}

export function ChatPage() {
  const messages = useStore(messagesStore);
  const composer = useStore(composerStore);
  const review = useStore(reviewStore);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const chat = useRef<Chat | null>(null);
  const { selected, tab } = useTabs(["ready", "soon"] as const, "ready");

  useEffect(() => {
    const created = createChat({ input: () => inputRef.current, messages: () => messagesRef.current });
    chat.current = created;
    const stop = created.watchMessages(messagesRef.current!);
    const stopLeaving = onLeave(created.dispose);   // уходим в другой раздел — чат успевает сохранить переписку
    created.init();
    return () => { stopLeaving(); stop(); created.dispose(); };
  }, []);

  const onStart = (code: string, label: string, event: MouseEvent) => {
    event.preventDefault();
    chat.current?.startFromLink(code, label);
  };

  return (
    <>
      <div className="chat">
        <section className="welcome">
          <h1>С чего начнём<br /><span>вашу работу сегодня?</span></h1>
          <p className="lead">{boot.aiEnabled
            ? "Задайте вопрос по праву, разберите документ, подготовьте заявление или рассчитайте госпошлину."
            : "Разберите документ, подготовьте заявление или рассчитайте госпошлину."}</p>
        </section>

        <div className="messages" id="messages" aria-live="polite" ref={messagesRef}>
          {messages.map((message) => <MessageView key={message.id} message={message} />)}
        </div>

        <div className="composer-wrap">
          <form className="composer" id="composer" autoComplete="off" aria-busy={review.busy}
            onSubmit={(event) => { event.preventDefault(); chat.current?.submit(); }}>
            <div className="flow-bar" hidden={composer.flow.hidden}>
              <span className="flow-title">{composer.flow.title}</span>
              <button type="button" onClick={() => chat.current?.cancelFlow()}>{composer.flow.cancel}</button>
            </div>
            <label className="visually-hidden" htmlFor="composer-input">Ваш вопрос</label>
            <textarea ref={inputRef} id="composer-input" rows={2} placeholder={composer.placeholder}
              onKeyDown={(event) => chat.current?.keyDown(event)} onInput={() => chat.current?.autoGrow()} />
            <div className="composer-row">
              <button type="button" className="icon-button" title="Загрузить документ: PDF или Word" aria-label="Загрузить документ"
                disabled={review.busy} onClick={() => chat.current?.attach()}><Icon name="clip" /></button>
              <span className="composer-mode"><Icon name="spark" />Помощник юриста</span>
              <span className="composer-hint">{composer.hint}</span>
              <button type="submit" className="send" aria-label="Отправить" disabled={review.busy}><Icon name="send" /></button>
            </div>
          </form>
        </div>

        <section className="start">
          <div className="start-heading">
            <h2 className="visually-hidden">Чем помочь?</h2>
            <div className="tabs" role="tablist" aria-label="Инструменты">
              <button {...tab("ready")} id="tools-ready" aria-controls="panel-ready">Быстрые действия</button>
              <button {...tab("soon")} id="tools-soon" aria-controls="panel-soon">В разработке</button>
            </div>
          </div>

          <div className={`actions-grid quick-actions${boot.aiEnabled ? " has-law" : ""}`} id="panel-ready" role="tabpanel"
            aria-labelledby="tools-ready" hidden={selected !== "ready"}>
            {boot.aiEnabled && (
              <a className="action action-law" href="/?start=law" onClick={(event) => onStart("law", "Вопрос по праву", event)}>
                <span className="action-visual" aria-hidden="true"><ActionArt kind="law" /><span className="action-arrow"><Icon name="arrow" /></span></span>
                <strong>Задать вопрос по праву</strong>
                <span className="desc">Ответ со ссылками на статьи закона</span>
              </a>
            )}
            <button className="action action-upload" type="button" onClick={(event) => onStart("review", "", event)}>
              <span className="action-visual" aria-hidden="true"><ActionArt kind="upload" /><span className="action-arrow"><Icon name="arrow" /></span></span>
              <strong>Загрузить документ</strong>
              <span className="desc">Выжимка, вопросы по тексту и правки</span>
            </button>
            <a className="action action-calc" href="/?start=duty" onClick={(event) => onStart("duty", "Посчитать госпошлину", event)}>
              <span className="action-visual" aria-hidden="true"><ActionArt kind="calc" /><span className="action-arrow"><Icon name="arrow" /></span></span>
              <strong>Рассчитать госпошлину</strong>
              <span className="desc">С формулой и ссылкой на НК РФ</span>
            </a>
          </div>

          <div className="actions-grid" id="panel-soon" role="tabpanel" aria-labelledby="tools-soon" hidden={selected !== "soon"}>
            <div className="action action-soon">
              <span className="action-icon"><Icon name="scales" /></span>
              <strong>Найти судебную практику</strong>
              <span className="desc">Суды Дагестана и Верховный Суд РФ</span>
            </div>
            {boot.soonDocuments.length > 0 && (
              <div className="action action-soon">
                <span className="action-icon"><Icon name="stack" /></span>
                <strong>Ещё {plural(boot.soonDocuments.length, "документ", "документа", "документов")}</strong>
                <span className="desc">Иски, ходатайства и заявления</span>
              </div>
            )}
          </div>

          <div className="section-head">
            <h2>Шаблоны документов</h2>
            <a href="/documents">Все шаблоны <Icon name="arrow" /></a>
          </div>
          <div className="sheets">
            <Sheets limit={5} soonLimit={0} onStart={onStart} />
            <a className="sheet-card sheet-more" href="/documents">
              <span className="sheet">
                <span className="sheet-more-arrow" aria-hidden="true"><Icon name="arrow" /></span>
                <Icon name="stack" />
                <span className="sheet-title">Все шаблоны документов</span>
              </span>
            </a>
          </div>
          <p className="disclaimer">Правосвод не заменяет консультацию юриста. Перед подачей в суд проверьте документ.</p>
        </section>
      </div>
      <PreviewDialog />
      <SourceDialog />
    </>
  );
}
